import { Alert, Box, Button, Tab, Tabs } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { DangerousGoodsDefinitionsPage } from '@tom-ui/planning'
import { Header } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import {
    DGSegregationRulesPage,
    DGStackingRulesPage,
    Permission,
    useYardStores,
} from '@tom-ui/yard'
import { observer } from 'mobx-react-lite'
import { TabPanel } from 'modules/planning/src/components/TabPanel'
import { SyntheticEvent, useState } from 'react'

const DG_SETTINGS_TAB_INDEX = {
  definitions: 0,
  stackingRules: 1,
  segregationRules: 2,
}

const DGSettingsPage = observer(() => {
  const [value, setValue] = useState(0)
  const { t } = useTranslate()
  const { authStore, dgSegregationRulesTableUIStore, dgStackingRulesTableUIStore } = useYardStores()
  const title = t('dgSettings', 'Dangerous Goods Settings')
  const breadcrumbs = [
    { label: t(PATHS.masterData.title), route: PATHS.masterData.url },
    {
      label: title,
    },
  ]

  const handleChange = (_e: SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const getTabInfoMessage = () => {
    if (value === DG_SETTINGS_TAB_INDEX.definitions) {
      return t(
        'dgSettingsDefinitionsInfo',
        'Define the UN numbers and IMO classes used to identify dangerous goods.',
      )
    }

    if (value === DG_SETTINGS_TAB_INDEX.stackingRules) {
      return t(
        'dgSettingsStackingRulesInfo',
        'Configure which IMO classes may be stacked on top of each other and the maximum allowed tier.',
      )
    }

    return t(
      'dgSettingsSegregationRulesInfo',
      'Configure prohibited IMO class combinations or the minimum separation distance in feet. Multiple cells can be selected by dragging the mouse.',
    )
  }

  const canResetRules = authStore.hasPermission(Permission.WriteDangerousGoodsSettings)
  const isStackingRulesTab = value === DG_SETTINGS_TAB_INDEX.stackingRules
  const isSegregationRulesTab = value === DG_SETTINGS_TAB_INDEX.segregationRules
  const showResetButton = canResetRules && (isStackingRulesTab || isSegregationRulesTab)
  const handleResetToDefaults = () => {
    if (isStackingRulesTab) {
      dgStackingRulesTableUIStore.toggleResetDialog()
      return
    }

    if (isSegregationRulesTab) {
      dgSegregationRulesTableUIStore.toggleResetDialog()
    }
  }

  return (
    <Box>
      <Header title={title} breadcrumbs={breadcrumbs} />
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Tabs value={value} onChange={handleChange} aria-label='basic tabs example'>
          <Tab label={t('dgDefinitions', 'Definitions')} sx={{ mx: 1 }} />
          <Tab label={t('dgStackingRules', 'Stacking Rules')} sx={{ mx: 1 }} />
          <Tab label={t('dgSegregationRules', 'Segregation Rules')} sx={{ mx: 1 }} />
        </Tabs>
        {showResetButton && (
          <Button variant='outlined' color='error' onClick={handleResetToDefaults}>
            {t('resetToDefaults', 'Reset to defaults')}
          </Button>
        )}
      </Box>
      <Alert severity='info' sx={{ my: 2 }}>
        {getTabInfoMessage()}
      </Alert>
      <TabPanel value={value} index={0}>
        <DangerousGoodsDefinitionsPage />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <DGStackingRulesPage />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <DGSegregationRulesPage />
      </TabPanel>
    </Box>
  )
})

export default DGSettingsPage
